import { useState, useEffect, useRef } from 'react';
import {
  getMultiFactorResolver,
  signOut,
  signInWithCredential,
  getAdditionalUserInfo,
  onAuthStateChanged,
  GoogleAuthProvider,
} from 'firebase/auth';
import type { MultiFactorError, MultiFactorResolver, User, OAuthCredential, AuthError } from 'firebase/auth';
import toast, { Toaster } from 'react-hot-toast';
import { auth } from './firebase';
import {
  buildRedirectUrl,
  buildCodeRedirectUrl,
  SsoHandoffError,
  persistRedirectBase,
  getRedirectInfo,
  shouldBounceStaleClient,
  clearStaleClientBounce,
} from './handoff';
import type { RedirectInfo } from './handoff';
import type { AuthHandoffPayload } from './auth/tokenContract';
import { SignInPanel } from './components/SignInPanel';
import { MfaPanel } from './components/MfaPanel';
import { LinkAccountPanel } from './components/LinkAccountPanel';
import { ErrorPanel } from './components/ErrorPanel';
import { LegacyBanner } from './components/LegacyBanner';
import { FallbackBanner } from './components/FallbackBanner';
import { ExtensionSunsetBanner } from './components/ExtensionSunsetBanner';
import { Tooltip } from './components/Tooltip';
import { RememberedAccountPanel } from './components/RememberedAccountPanel';
import {
  readSavedProfile,
  writeSavedProfile,
  clearSavedProfile,
} from './storage/savedProfile';
import type { SavedProfile, LastProvider } from './storage/savedProfile';

const ALLOWED_DOMAIN = (import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN as string) || 'sellersflow.com';

type Phase =
  | { tag: 'loading' }
  | { tag: 'idle' }
  | { tag: 'remembered'; profile: SavedProfile; busy: boolean }
  | { tag: 'mfa'; resolver: MultiFactorResolver; remember: boolean; provider: LastProvider }
  | { tag: 'link'; email: string; credential: OAuthCredential }
  | { tag: 'error'; message: string }
  | { tag: 'blocked'; base: string }
  | { tag: 'needs-update'; reason: 'extension-sunset' | 'unmigrated-client'; destination: string };

export default function App() {
  const [phase, setPhase] = useState<Phase>({ tag: 'loading' });
  const [flashError, setFlashError] = useState(false);
  const [prefillEmail, setPrefillEmail] = useState<string | null>(null);
  const [redirectInfo, setRedirectInfo] = useState<RedirectInfo | null>(null);
  const flashTimerRef = useRef<number | null>(null);

  function flashSignInError(message: string) {
    toast.error(message, { id: 'sf-signin-error' });
    setFlashError(true);
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => setFlashError(false), 3000);
  }

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    };
  }, []);

  async function handleUser(
    user: User,
    opts?: {
      remember?: boolean;
      provider?: LastProvider;
      profile?: { displayName?: string | null; photoURL?: string | null };
    },
  ) {
    const email = user.email ?? '';
    if (!email.endsWith(`@${ALLOWED_DOMAIN}`)) {
      clearSavedProfile();
      await signOut(auth);
      setPhase({ tag: 'error', message: 'This sign-in is restricted to our internal team.' });
      return;
    }

    const displayName = user.displayName ?? opts?.profile?.displayName ?? null;
    const photoURL = user.photoURL ?? opts?.profile?.photoURL ?? null;

    if (opts?.remember) {
      const inferred: LastProvider =
        user.providerData[0]?.providerId === 'google.com' ? 'google' : 'password';
      writeSavedProfile({
        uid: user.uid,
        email,
        displayName,
        photoURL,
        lastProvider: opts.provider ?? inferred,
      });
    } else if (opts?.remember === false) {
      clearSavedProfile();
    }

    const info = getRedirectInfo();

    if (info.clientId) {
      // SSO v2 — code+PKCE via SF-DataFlow-API. Neither this Firebase ID
      // token nor its refresh token ever reaches the redirect_uri; only a
      // single-use code does. See handoff.ts's buildCodeRedirectUrl.
      const idToken = await user.getIdToken(true);
      try {
        window.location.href = await buildCodeRedirectUrl(idToken);
      } catch (err) {
        if (err instanceof SsoHandoffError && err.code === 'USER_NOT_PROVISIONED') {
          clearSavedProfile();
          await signOut(auth);
          setPhase({
            tag: 'error',
            message: 'This account is not provisioned for this app yet. Contact your administrator.',
          });
        } else {
          setPhase({
            tag: 'error',
            message: err instanceof Error ? err.message : 'Sign-in failed. Please try again.',
          });
        }
      }
      return;
    }

    // Legacy #payload= fragment handoff — extension only, see allowlist.ts.
    // getRedirectInfo() already refuses this destination up front (App.tsx's
    // load effect) when it isn't eligible, so reaching here means it is.
    const idTokenResult = await user.getIdTokenResult(true);
    const payload: AuthHandoffPayload = {
      idToken: idTokenResult.token,
      refreshToken: user.refreshToken,
      expiresIn: String(
        Math.round((new Date(idTokenResult.expirationTime).getTime() - Date.now()) / 1000),
      ),
      uid: user.uid,
      email,
      displayName,
      photoURL,
      signedInAt: Date.now(),
    };

    window.location.href = buildRedirectUrl(payload);
  }

  function handleMfaError(error: unknown, opts: { remember: boolean; provider: LastProvider }) {
    try {
      const resolver = getMultiFactorResolver(auth, error as MultiFactorError);
      setPhase({ tag: 'mfa', resolver, remember: opts.remember, provider: opts.provider });
    } catch {
      setPhase({ tag: 'error', message: 'Multi-factor authentication failed to initialize.' });
    }
  }

  async function continueAsRemembered(profile: SavedProfile) {
    setPhase((p) => (p.tag === 'remembered' ? { ...p, busy: true } : p));
    const u = auth.currentUser;
    if (u && u.uid === profile.uid) {
      try {
        await handleUser(u, { remember: true, provider: profile.lastProvider });
        return;
      } catch {
        /* fall through to fresh sign-in */
      }
    }
    setPrefillEmail(profile.email);
    setPhase({ tag: 'idle' });
  }

  // A never-before-seen URL is a guaranteed cache miss at every layer between
  // here and the origin server (browser HTTP cache, any CDN/proxy in front
  // of the destination) — unlike window.location.reload(), which only
  // re-requests THIS page (auth.sellersflow.com) and does nothing for a
  // stale bundle cached on a completely different origin.
  function cacheBustedUrl(base: string): string {
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}_sso_retry=${Date.now()}`;
  }

  async function useDifferentAccount() {
    try { await signOut(auth); } catch { /* noop */ }
    clearSavedProfile();
    setPrefillEmail(null);
    setPhase({ tag: 'idle' });
  }

  // On load: persist redirect_uri and check for Google OAuth id_token in URL fragment.
  useEffect(() => {
    // Persist FIRST: getRedirectInfo prefers sessionStorage over the URL, so
    // reading before persisting would describe the PREVIOUS request whenever
    // this load carries fresh sign-in params — and every gate below
    // (blocked / needs-update / bounce) would act on the wrong destination.
    persistRedirectBase();
    const info = getRedirectInfo();
    setRedirectInfo(info);

    // Explicitly requested destination not on the allowlist: refuse to offer
    // sign-in at all. Fallback (no redirect_uri supplied) is handled by the
    // panels themselves — "no destination" warning with sign-in disabled.
    if (!info.allowed && info.source !== 'fallback') {
      setPhase({ tag: 'blocked', base: info.base });
      return;
    }

    // No client_id (not on SSO v2 yet) AND not eligible for the legacy
    // fragment handoff either (extension-only, and/or past its sunset — see
    // allowlist.ts). Refuse before offering sign-in at all, same as the
    // `blocked` check above and for the same reason: don't run a Google/MFA
    // round trip for a request we're going to refuse to hand off anyway.
    if (!info.clientId && !info.legacyEligible && info.source !== 'fallback') {
      if (!info.isExtension && shouldBounceStaleClient()) {
        // Legacy-style request from a WEB host = the user's browser cached a
        // pre-SSO-v2 bundle of an app that has migrated. Send them back to
        // the requested URL once — the freshly-served bundle starts a proper
        // code+PKCE sign-in itself. Repeats inside the guard window fall
        // through to the screen below (see handoff.ts).
        window.location.replace(info.base);
        return;
      }
      setPhase({
        tag: 'needs-update',
        reason: info.isExtension ? 'extension-sunset' : 'unmigrated-client',
        destination: info.base,
      });
      return;
    }
    // A well-formed SSO v2 request arrived — any stale-client bounce credit
    // can be reset for the next incident.
    if (info.clientId) clearStaleClientBounce();

    const hash = window.location.hash;
    const cleanHash = () => {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    };

    // OAuth error in URL fragment (user denied, etc.)
    if (hash.includes('error=')) {
      const params = new URLSearchParams(hash.slice(1));
      const err = params.get('error') ?? 'unknown_error';
      const desc = params.get('error_description') ?? '';
      cleanHash();
      sessionStorage.removeItem('sf_oauth_state');
      sessionStorage.removeItem('sf_oauth_nonce');
      if (err === 'access_denied' || err === 'user_cancelled') {
        setPhase({ tag: 'idle' });
      } else {
        setPhase({ tag: 'error', message: desc || `Google sign-in failed: ${err}` });
      }
      return;
    }

    // Successful OAuth callback: id_token in fragment
    if (hash.includes('id_token=')) {
      const params = new URLSearchParams(hash.slice(1));
      const idToken = params.get('id_token');
      const returnedState = params.get('state');
      const expectedState = sessionStorage.getItem('sf_oauth_state');
      cleanHash();
      sessionStorage.removeItem('sf_oauth_state');
      sessionStorage.removeItem('sf_oauth_nonce');

      if (!idToken) {
        setPhase({ tag: 'error', message: 'Google sign-in returned no token. Try again.' });
        return;
      }
      if (!returnedState || returnedState !== expectedState) {
        setPhase({ tag: 'error', message: 'Sign-in state mismatch. Try again.' });
        return;
      }

      const credential = GoogleAuthProvider.credential(idToken);
      signInWithCredential(auth, credential)
        .then(async (result) => {
          const info = getAdditionalUserInfo(result);
          if (info?.isNewUser) {
            // No existing account for this @sellersflow.com email — don't create one here.
            try { await result.user.delete(); } catch { await signOut(auth); }
            setPhase({ tag: 'error', message: 'No account found for this email. Contact your administrator.' });
            return;
          }
          const p = info?.profile as { name?: string; picture?: string } | undefined;
          await handleUser(result.user, {
            remember: true,
            provider: 'google',
            profile: { displayName: p?.name ?? null, photoURL: p?.picture ?? null },
          });
        })
        .catch((error: unknown) => {
          const code = (error as { code?: string }).code;
          if (code === 'auth/multi-factor-auth-required') {
            handleMfaError(error, { remember: true, provider: 'google' });
          } else if (code === 'auth/account-exists-with-different-credential') {
            const authErr = error as AuthError;
            const email = (authErr.customData?.email as string | undefined) ?? '';
            const cred = GoogleAuthProvider.credentialFromError(authErr) ?? credential;
            if (email && cred) {
              setPhase({ tag: 'link', email, credential: cred });
            } else {
              setPhase({ tag: 'error', message: 'Sign-in failed. Please use email & password.' });
            }
          } else if (code === 'auth/user-disabled') {
            setPhase({ tag: 'error', message: 'This account has been disabled.' });
          } else if (code === 'auth/network-request-failed') {
            setPhase({ tag: 'error', message: 'Network error. Check your connection and try again.' });
          } else {
            const msg = error instanceof Error ? error.message : 'Sign-in failed. Please try again.';
            setPhase({ tag: 'error', message: code ? `${msg} (${code})` : msg });
          }
        });
      return;
    }

    const saved = readSavedProfile();
    let unsub: (() => void) | null = null;
    let resolved = false;
    unsub = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      try { unsub?.(); } catch { /* noop */ }
      if (saved && (!user || user.uid === saved.uid)) {
        setPhase({ tag: 'remembered', profile: saved, busy: false });
      } else {
        setPhase({ tag: 'idle' });
      }
    });
    return () => { try { unsub?.(); } catch { /* noop */ } };
  }, []);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-off-white px-4 dark:bg-sf-dark-gunmetal">
      <img
        src="/auth-sellersflow.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none fixed bottom-4 left-4 h-24 w-auto select-none opacity-15 dark:opacity-20"
      />
      <div className="relative w-full max-w-sm">
        {redirectInfo?.legacy && <LegacyBanner redirectInfo={redirectInfo} />}
        {redirectInfo?.source === 'fallback' && <FallbackBanner />}
        {redirectInfo && !redirectInfo.clientId && redirectInfo.isExtension && redirectInfo.legacyEligible && (
          <ExtensionSunsetBanner />
        )}
        <div
          className={`rounded-2xl border bg-white p-8 shadow-xl transition-colors duration-200 dark:bg-white/5 dark:shadow-2xl dark:backdrop-blur-sm ${
            flashError
              ? 'border-red-500 shadow-red-500/20 dark:border-red-500'
              : 'border-gray-200 dark:border-white/10'
          }`}
        >
          <div className="mb-6 flex justify-center">
            <img
              draggable={false}
              src="/logo-light.png"
              alt="SellersFlow"
              className="h-8 object-contain dark:hidden"
            />
            <img
              draggable={false}
              src="/logo-dark.png"
              alt="SellersFlow"
              className="hidden h-8 object-contain dark:block"
            />
          </div>
          {phase.tag === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-sf-blue-vibrant border-t-transparent" />
              <p className="text-sm text-gray-400">Loading…</p>
            </div>
          )}

          {phase.tag === 'idle' && (
            <SignInPanel
              onUser={handleUser}
              onMfa={handleMfaError}
              onError={(msg) => setPhase({ tag: 'error', message: msg })}
              onSignInFailure={flashSignInError}
              initialEmail={prefillEmail ?? undefined}
              loginHint={prefillEmail ?? undefined}
              redirectInfo={redirectInfo}
            />
          )}

          {phase.tag === 'remembered' && (
            <RememberedAccountPanel
              profile={phase.profile}
              onContinue={() => continueAsRemembered(phase.profile)}
              onUseDifferent={useDifferentAccount}
              loading={phase.busy}
              redirectInfo={redirectInfo}
            />
          )}

          {phase.tag === 'mfa' && (
            <MfaPanel
              resolver={phase.resolver}
              remember={phase.remember}
              provider={phase.provider}
              onUser={handleUser}
              onError={(msg) => setPhase({ tag: 'error', message: msg })}
              onSignInFailure={flashSignInError}
              onBack={() => setPhase({ tag: 'idle' })}
            />
          )}

          {phase.tag === 'link' && (
            <LinkAccountPanel
              email={phase.email}
              credential={phase.credential}
              onUser={handleUser}
              onError={(msg) => setPhase({ tag: 'error', message: msg })}
              onSignInFailure={flashSignInError}
              onBack={() => setPhase({ tag: 'idle' })}
            />
          )}

          {phase.tag === 'error' && (
            <ErrorPanel message={phase.message} onRetry={() => setPhase({ tag: 'idle' })} />
          )}

          {phase.tag === 'blocked' && (
            <ErrorPanel
              title="Sign-in blocked"
              message={
                <>
                  This sign-in link points to an{' '}
                  <Tooltip label={phase.base}>
                    <button
                      type="button"
                      className="cursor-help font-semibold text-gray-600 underline decoration-dotted underline-offset-2 dark:text-gray-300"
                    >
                      unregistered destination
                    </button>
                  </Tooltip>{' '}
                  and has been blocked. Launch sign-in from the SellersFlow app or resource
                  you&apos;re trying to access.
                </>
              }
              onRetry={() => window.history.back()}
              retryLabel="Go back"
              secondaryLabel="Reload"
              onSecondary={() => window.location.reload()}
            />
          )}

          {phase.tag === 'needs-update' && (
            <ErrorPanel
              title={phase.reason === 'extension-sunset' ? 'Update required' : 'App out of date'}
              message={
                phase.reason === 'extension-sunset' ? (
                  <>
                    Your SellersFlow extension is out of date and can no longer sign in this way.
                    Please update it from the Chrome Web Store, then try again.
                  </>
                ) : (
                  <>
                    Your browser is running an outdated, cached version of this app, which uses a
                    retired sign-in method. Click below to force-reload it — if this keeps
                    happening, contact the SellersFlow platform team.
                  </>
                )
              }
              // unmigrated-client: goes to the DESTINATION app (cache-busted),
              // not this auth page — reloading auth.sellersflow.com itself
              // never touches the stale bundle actually causing the loop.
              // extension-sunset: destination is a chromiumapp.org redirect
              // target, not a real page — nothing useful to navigate to, so
              // this just reloads auth's own page as before.
              onRetry={() =>
                phase.reason === 'extension-sunset'
                  ? window.location.reload()
                  : window.location.replace(cacheBustedUrl(phase.destination))
              }
              retryLabel={phase.reason === 'extension-sunset' ? 'Reload' : 'Reload the app'}
            />
          )}
        </div>

        <p className="mt-6 text-center text-[11px] leading-relaxed text-gray-400 dark:text-gray-600">
          ©&nbsp;{new Date().getFullYear()}&nbsp;
          <a
            href="https://sellersflow.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 underline-offset-2 transition hover:text-sf-blue-vibrant hover:underline dark:text-gray-500 dark:hover:text-sf-blue-vibrant"
          >
            SellersFlow LLC
          </a>
          . All rights reserved.
          <br />
          Unauthorized access is prohibited and may be prosecuted.
        </p>
      </div>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          error: {
            style: {
              background: '#fef2f2',
              color: '#b91c1c',
              border: '1px solid #ef4444',
              fontSize: '14px',
              fontWeight: 500,
            },
            iconTheme: { primary: '#ef4444', secondary: '#fff' },
          },
        }}
      />
    </div>
  );
}
